import clsx from "clsx";
import React, { useState } from "react";

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Clear,
  Code,
  ContentCopy,
  DeleteOutline,
  ExpandLess,
  ExpandMore,
  LayersOutlined,
  OpenInNew,
  Refresh,
  StorageOutlined,
} from "@mui/icons-material";
import { t } from "ttag";
import { useStoreActions } from "../../../models/RootStore";
import useStyles from "./DevToolsDialog.style";

const HANDLER_TYPE_COLORS: Record<string, any> = {
  control: "primary",
  service: "secondary",
  sensor: "success",
  outdoor: "warning",
};

type DevToolsOption = "cache" | "clearCache" | null;

interface Props {
  open: boolean;
  onClose: () => void;
  activeDevice: { id: string; serial: string } | null;
}

export default function DevToolsDialog({ open, onClose, activeDevice }: Props) {
  const styles: any = useStyles();
  const getDeviceCache = useStoreActions((actions) => actions.devices.getDeviceCache);
  const clearDeviceCache = useStoreActions((actions) => actions.devices.clearDeviceCache);
  const { addMessage } = useStoreActions((action) => action.errorMessage);

  const [activeOption, setActiveOption] = useState<DevToolsOption>(null);
  const [cacheResponse, setCacheResponse] = useState<any>(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const [clearLine, setClearLine] = useState<string>("all");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);

  const handleClose = () => {
    setActiveOption(null);
    setCacheResponse(null);
    setShowRawJson(false);
    setClearLine("all");
    setShowClearConfirm(false);
    onClose();
  };

  const loadCache = async (id: string) => {
    setCacheLoading(true);
    setCacheResponse(null);
    setShowRawJson(false);
    try {
      const response = await getDeviceCache(id);
      setCacheResponse(response);
    } catch (e: any) {
      addMessage({ message: e?.message || t`Failed to fetch cache data` });
    } finally {
      setCacheLoading(false);
    }
  };

  const handleSelectOption = (option: DevToolsOption) => {
    setActiveOption(option);
    if (option !== "clearCache") setShowClearConfirm(false);
    if (option === "cache" && activeDevice && cacheResponse === null && !cacheLoading) {
      loadCache(activeDevice.id);
    }
  };

  const handleConfirmClear = async () => {
    if (!activeDevice) return;
    setClearing(true);
    const line = clearLine === "all" ? undefined : parseInt(clearLine, 10);
    try {
      await clearDeviceCache({ id: activeDevice.id, line });
      setShowClearConfirm(false);
      if (cacheResponse !== null) await loadCache(activeDevice.id);
    } catch (e: any) {
      addMessage({ message: e?.message || t`Failed to clear cache` });
    } finally {
      setClearing(false);
    }
  };

  const cacheKeys: string[] = cacheResponse?.keys || [];
  const cacheData: any = cacheResponse?.handlerData || null;

  const copyToClipboard = () =>
    navigator.clipboard.writeText(JSON.stringify(cacheData, null, 2));

  const openRawJson = () => {
    const blob = new Blob(
      [JSON.stringify(cacheData, null, 2)],
      { type: "application/json" }
    );
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const renderRightPanel = () => {
    if (!activeOption) {
      return (
        <div className={styles.emptyState}>
          <Code className={styles.emptyStateIcon} color="disabled" />
          <Typography variant="body2" className={styles.emptyStateText}>
            {t`Select an option from the left`}
          </Typography>
        </div>
      );
    }

    if (activeOption === "cache") {
      return (
        <div className={styles.panelPadding}>
          <div className={styles.panelHeader}>
            <Typography variant="subtitle2" className={styles.panelTitle}>
              {t`View Cache`}
            </Typography>
            {cacheResponse !== null && !cacheLoading && (
              <IconButton
                size="small"
                onClick={() => activeDevice && loadCache(activeDevice.id)}
              >
                <Refresh className={styles.refreshIcon} />
              </IconButton>
            )}
          </div>

          {cacheLoading ? (
            <div className={styles.loadingState}>
              <CircularProgress size={22} thickness={3} />
              <Typography variant="caption" color="text.secondary">
                {t`Loading cache data…`}
              </Typography>
            </div>
          ) : cacheKeys.length === 0 ? (
            <div className={styles.emptyDataState}>
              <StorageOutlined className={styles.emptyDataIcon} color="disabled" />
              <Typography variant="body2" color="text.secondary" className={styles.smallText}>
                {t`No cache data found for this device.`}
              </Typography>
            </div>
          ) : (
            <>
              <div className={styles.summaryChips}>
                <Chip
                  label={`${Object.keys(cacheData || {}).length} lines`}
                  size="small"
                  variant="outlined"
                  className={styles.summaryChipItem}
                />
                <Chip
                  label={`${cacheKeys.length} keys`}
                  size="small"
                  className={styles.summaryChipItem}
                />
              </div>

              <div className={styles.lineList}>
                {Object.entries(cacheData || {})
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([line, types]: [string, any]) => (
                    <div key={line} className={styles.lineRow}>
                      <Typography className={styles.lineLabel} color="text.secondary">
                        Line {line}
                      </Typography>
                      <div className={styles.chipRow}>
                        {Object.keys(types).map((type) => {
                          const handlerData = types[type];
                          const isEmpty =
                            handlerData !== null &&
                            typeof handlerData === "object" &&
                            Object.keys(handlerData).length === 0;
                          return (
                            <span key={type} className={styles.chipWrapper}>
                              <Chip
                                label={isEmpty ? `${type} (empty)` : type}
                                size="small"
                                color={
                                  isEmpty
                                    ? "default"
                                    : (HANDLER_TYPE_COLORS[type] || "default")
                                }
                                variant="outlined"
                                className={clsx(
                                  styles.handlerChip,
                                  isEmpty && styles.emptyHandlerChip
                                )}
                              />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
              </div>

              <Divider className={styles.cacheDivider} />
              <div className={styles.jsonToolbar}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={
                    showRawJson
                      ? <ExpandLess className={styles.expandIcon} />
                      : <ExpandMore className={styles.expandIcon} />
                  }
                  onClick={() => setShowRawJson((v) => !v)}
                  className={styles.jsonToggleBtn}
                >
                  {showRawJson ? t`Hide Raw JSON` : t`Show Raw JSON`}
                </Button>
                <div className={styles.iconGroup}>
                  <IconButton size="small" onClick={copyToClipboard}>
                    <ContentCopy className={styles.actionIcon} />
                  </IconButton>
                  <IconButton size="small" onClick={openRawJson}>
                    <OpenInNew className={styles.actionIcon} />
                  </IconButton>
                </div>
              </div>
              {showRawJson && (
                <pre className={styles.cacheDataPre}>
                  {JSON.stringify(cacheData, null, 2)}
                </pre>
              )}
            </>
          )}
        </div>
      );
    }

    if (activeOption === "clearCache") {
      return (
        <div className={styles.panelPadding}>
          <Typography variant="subtitle2" className={styles.clearTitle}>
            {t`Clear Cache`}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            className={styles.clearSubtext}
          >
            {t`Clears handler cache data from Redis. Handlers will restart automatically.`}
          </Typography>

          <div className={styles.clearRow}>
            <select
              className={styles.clearLineSelect}
              value={clearLine}
              onChange={(e) => {
                setClearLine(e.target.value);
                setShowClearConfirm(false);
              }}
            >
              <option value="all">{t`All lines`}</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={String(n)}>{`Line ${n}`}</option>
              ))}
            </select>

            {!showClearConfirm && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setShowClearConfirm(true)}
                className={styles.clearBtn}
              >
                {t`Clear`}
              </Button>
            )}
          </div>

          {showClearConfirm && (
            <div className={styles.confirmBox}>
              <Typography variant="body2" className={styles.confirmText}>
                {clearLine === "all"
                  ? t`Clear all handler cache data for this device?`
                  : `Clear handler cache for line ${clearLine}?`}
              </Typography>
              <div className={styles.confirmActions}>
                <Button
                  size="small"
                  onClick={() => setShowClearConfirm(false)}
                  disabled={clearing}
                >
                  {t`Cancel`}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleConfirmClear}
                  disabled={clearing}
                  startIcon={
                    clearing
                      ? <CircularProgress size={12} color="inherit" />
                      : undefined
                  }
                >
                  {clearing ? t`Clearing…` : t`Confirm`}
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ className: styles.dialogPaper }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <span>
          {t`Dev Tools`}
          <Typography component="span" className={styles.titleSerial}>
            {activeDevice?.serial}
          </Typography>
        </span>
        <IconButton onClick={handleClose} size="small">
          <Clear />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <div className={styles.sideNav}>
          <div className={styles.navSectionLabel}>Cache</div>
          <List disablePadding dense className={styles.navList}>
            <ListItemButton
              selected={activeOption === "cache"}
              onClick={() => handleSelectOption("cache")}
              className={styles.navItemCache}
            >
              <ListItemIcon className={styles.navItemIcon}>
                <LayersOutlined
                  className={styles.navIcon}
                  color={activeOption === "cache" ? "primary" : "disabled"}
                />
              </ListItemIcon>
              <ListItemText
                primary={t`View Cache`}
                primaryTypographyProps={{ className: styles.navItemText }}
              />
            </ListItemButton>

            <ListItemButton
              selected={activeOption === "clearCache"}
              onClick={() => handleSelectOption("clearCache")}
              className={styles.navItem}
            >
              <ListItemIcon className={styles.navItemIcon}>
                <DeleteOutline
                  className={styles.navIcon}
                  color={activeOption === "clearCache" ? "primary" : "disabled"}
                />
              </ListItemIcon>
              <ListItemText
                primary={t`Clear Cache`}
                primaryTypographyProps={{ className: styles.navItemText }}
              />
            </ListItemButton>
          </List>
        </div>

        <div className={styles.rightPanel}>
          {renderRightPanel()}
        </div>
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button onClick={handleClose} color="primary">{t`Close`}</Button>
      </DialogActions>
    </Dialog>
  );
}
